import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';

interface ComparisonData {
  assignmentsCreated: { today: number; yesterday: number };
  vehiclesReturned: { today: number; yesterday: number };
  workshopEntries: { today: number; yesterday: number };
}

interface TodayVsYesterdayProps {
  data: ComparisonData;
}

export const TodayVsYesterday: React.FC<TodayVsYesterdayProps> = ({ data }) => {
  const getChange = (today: number, yesterday: number) => {
    if (yesterday === 0) return today > 0 ? 100 : 0;
    return ((today - yesterday) / yesterday) * 100;
  };

  const renderStat = (title: string, today: number, yesterday: number) => {
    const change = getChange(today, yesterday);
    const isPositive = change >= 0;
    
    return (
      <View style={styles.statRow}>
        <Text style={styles.statTitle}>{title}</Text>
        <View style={styles.statValues}>
          <Text style={styles.todayValue}>{today}</Text>
          <View style={[
            styles.changeBadge,
            { backgroundColor: isPositive ? theme.colors.green600 : theme.colors.red600 }
          ]}>
            <Ionicons 
              name={isPositive ? 'arrow-up' : 'arrow-down'} 
              size={12} 
              color={theme.colors.white} 
            />
            <Text style={styles.changeText}>{Math.abs(change).toFixed(0)}%</Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Ionicons name="calendar" size={20} color={theme.colors.primary} />
        <Text style={styles.title}>Today vs Yesterday</Text>
      </View>
      
      {renderStat('Assignments Created', data.assignmentsCreated.today, data.assignmentsCreated.yesterday)}
      {renderStat('Vehicles Returned', data.vehiclesReturned.today, data.vehiclesReturned.yesterday)}
      {renderStat('Workshop Entries', data.workshopEntries.today, data.workshopEntries.yesterday)}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    ...theme.shadows.md,
    marginBottom: theme.spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
    paddingBottom: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.xs,
  },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  statTitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    flex: 1,
  },
  statValues: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  todayValue: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
    minWidth: 30,
    textAlign: 'right',
  },
  changeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: theme.borderRadius.sm,
    minWidth: 50,
    justifyContent: 'center',
  },
  changeText: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '600' as '600',
    color: theme.colors.white,
    marginLeft: 2,
  },
});
